import { useGetPendingLeadsQuery, LEADS_PAGE_SIZE } from '@/services/leads';
import { LeadTableRow } from '@/types/Leads';
import { Box, Card, Column, Icon, Pagination } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';

import { LeadManagementFilter } from './LeadManagementFilter';
import { LeadManagementLoading } from './LeadManagementLoading';
import { LeadManagementRowActionsCell } from './LeadManagementRowActionsCell';
import { LeadManagementTable } from './LeadManagementTable';
import { LeadManagementTimeToAccept } from './LeadManagementTimeToAccept';
import { useLeadTabParams } from './useLeadTabParams';

const primaryColorStyle: React.CSSProperties = { color: 'var(--btn-bg-primary-idle)' };
const prevPageIcon = <Icon name="caret-left" size="sm" style={primaryColorStyle} />;
const nextPageIcon = <Icon name="caret-right" size="sm" style={primaryColorStyle} />;

const renderTimeToAccept = (cell?: any) => (cell ? <LeadManagementTimeToAccept expiresAt={cell as string} /> : null);

export function LeadManagementPendingAcceptanceTab() {
  const {
    queryArgs,
    page,
    searchTerm,
    appliedSort,
    activeFilterCount,
    currentFilters,
    handleSortSelection,
    handlePageChange,
    handleSearchChange,
    handleApplyFilters,
  } = useLeadTabParams();
  const { data, isLoading } = useGetPendingLeadsQuery(queryArgs);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const handleDismissLead = useCallback((leadId: string) => {
    setDismissedIds((prev) => new Set([...prev, leadId]));
  }, []);

  const leads = useMemo(
    () => (data?.leads ?? []).filter((l) => !dismissedIds.has(l.id)),
    [data?.leads, dismissedIds],
  );

  const pendingExtraColumns = useMemo<Column[]>(() => {
    return [{ heading: 'Time to Accept', dataKey: 'expiresAt', render: renderTimeToAccept }];
  }, []);

  const acceptActionColumn = useCallback(
    (lead: LeadTableRow) => {
      const location = lead ? `${lead.address.locality}, ${lead.address.region} ${lead.address.postalCode}` : '';
      return <LeadManagementRowActionsCell lead={lead} location={location} onSuccess={handleDismissLead} />;
    },
    [handleDismissLead],
  );

  const total = Math.max(0, (data?.total ?? 0) - dismissedIds.size);

  return (
    <Card padding="xs" marginTop="0">
      <Card.Header>
        <Box direction="row" justifyContent="space-between" alignItems="center">
          <Box fontSize="xl" fontWeight="medium">
            Pending Acceptance
          </Box>
        </Box>
      </Card.Header>
      <Box margin="0 lg lg lg" direction="column" childGap="md">
        <LeadManagementFilter
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          activeFilterCount={activeFilterCount}
          currentFilters={currentFilters}
          onApplyFilters={handleApplyFilters}
          appliedSort={appliedSort}
          onSortChange={handleSortSelection}
        />
        <Box overflow="hidden" radius="md">
          {isLoading ? (
            <Box padding="md">
              <LeadManagementLoading />
            </Box>
          ) : (
            <LeadManagementTable
              leads={leads}
              extraColumns={pendingExtraColumns}
              renderActions={acceptActionColumn}
            />
          )}
        </Box>
        {total > LEADS_PAGE_SIZE && (
          <Pagination
            activePage={page}
            arePagesVisible
            itemsPerPage={LEADS_PAGE_SIZE}
            numberOfPagesDisplayed={3}
            onChange={handlePageChange}
            totalItemsCount={total}
            isCompact
            prevPageText={prevPageIcon}
            nextPageText={nextPageIcon}
          />
        )}
      </Box>
    </Card>
  );
}
